import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { getCurrentPricing, subscribeToPricing, getDiscountPercentage } from '../../../constants/pricing';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

// Helper function to calculate price with GST (only for INR)
const calculatePriceWithGST = (price, gstPercentage, currency) => {
  if (currency !== 'INR') return price;
  if (!gstPercentage || gstPercentage <= 0) return price;
  return price + (price * gstPercentage / 100);
};

const SubscriptionPanel = ({ userProfile, paymentHistory }) => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  
  const isPaidUser = userProfile?.subscription_status === 'paid';
  const lastPayment = paymentHistory?.find(p => p?.payment_status === 'captured');

  // Fetch pricing on mount and subscribe to updates
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const currentPricing = getCurrentPricing();
        setPricing(currentPricing);
        setPricingLoading(false);
      } catch (error) {
        console.error('Error fetching pricing:', error);
        setPricingLoading(false);
      }
    };

    fetchPricing();

    // Subscribe to real-time pricing updates
    const unsubscribe = subscribeToPricing((updatedPricing) => {
      console.log('SubscriptionPanel: Pricing updated', updatedPricing);
      setPricing(updatedPricing);
    });

    return () => unsubscribe();
  }, []);

  // Get dynamic pricing values
  const getDynamicPriceData = () => {
    if (!pricing || pricingLoading) {
      return {
        usdOriginal: 298,
        usdOffer: 149,
        inrOriginal: 27998,
        inrOffer: 13999,
        gstPercentage: 18,
        discountPercent: 50,
        badgeText: 'LAUNCH OFFER',
        features: [
          'AI + Human expert review',
          'Comprehensive analysis',
          'Priority processing (24-48h)',
          'Detailed recommendations',
          'Direct expert consultation',
        ]
      };
    }

    const usdData = pricing.paid.USD;
    const inrData = pricing.paid.INR;
    const discountPercent = getDiscountPercentage('USD');
    
    return {
      usdOriginal: usdData.original,
      usdOffer: usdData.offer,
      inrOriginal: inrData.original,
      inrOffer: inrData.offer,
      gstPercentage: pricing.paid.INR?.gst || null,
      discountPercent: discountPercent,
      badgeText: pricing.paid.badge || 'LAUNCH OFFER',
      features: pricing.paid.features || [
        'AI + Human expert review',
        'Comprehensive analysis',
        'Priority processing (24-48h)',
        'Detailed recommendations',
        'Direct expert consultation',
      ]
    };
  };

  const priceData = getDynamicPriceData();
  
  // Calculate prices with GST for INR
  const finalUsdOffer = priceData.usdOffer;
  const finalUsdOriginal = priceData.usdOriginal;
  const finalInrOffer = calculatePriceWithGST(priceData.inrOffer, priceData.gstPercentage, 'INR');
  const finalInrOriginal = calculatePriceWithGST(priceData.inrOriginal, priceData.gstPercentage, 'INR');
  
  const usdSavings = finalUsdOriginal - finalUsdOffer;
  const inrSavings = finalInrOriginal - finalInrOffer;

  // Free plan features
  const freeFeatures = pricing?.free?.features?.length > 0 
    ? pricing.free.features 
    : [
        'AI-powered deck analysis',
        'Basic DecX score',
        'Standard processing time',
        'Email support',
      ];

  // Loading state
  if (pricingLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#004AAD', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p className="text-muted-foreground">Loading pricing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">

      {/* ── Header ── */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Zap" size={20} />
          Subscription Plan
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your subscription and billing cycle</p>
      </div>

      {/* ── Current Plan Status ── */}
      <div className="bg-muted/30 border border-border rounded-lg p-5 mb-6">
        <div className="flex items-start gap-4">
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 48, height: 48, borderRadius: 8, flexShrink: 0,
            background: isPaidUser ? GRAD : 'var(--muted, #f8fafc)',
          }}>
            <Icon
              name={isPaidUser ? 'Crown' : 'User'}
              size={24}
              color={isPaidUser ? '#ffffff' : 'var(--muted-foreground, #94a3b8)'}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-foreground">
                {isPaidUser ? 'Premium Plan' : 'Free Plan'}
              </h3>
              {isPaidUser && (
                <span style={{
                  padding: '2px 10px', borderRadius: 100,
                  background: GRAD, color: '#fff',
                  fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                }}>
                  Active
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {isPaidUser
                ? 'AI + Human powered deck audits with priority processing'
                : 'Basic AI-powered deck analysis'}
            </p>
            {isPaidUser && lastPayment && (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>Last payment: {new Date(lastPayment?.paid_at)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="DollarSign" size={14} />
                  <span>Amount: {lastPayment?.currency === 'INR' ? '₹' : '$'}{(lastPayment?.amount / 100)?.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Plan Comparison Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* FREE CARD */}
        <div style={{
          position: 'relative', borderRadius: 14, padding: 24,
          border: !isPaidUser ? '2px solid transparent' : '1px solid var(--border, #e2e8f0)',
          background: !isPaidUser
            ? `linear-gradient(var(--card, #fff), var(--card, #fff)) padding-box, ${GRAD} border-box`
            : 'var(--card, #fff)',
          transition: 'all 0.15s',
        }}>
          {/* Current badge */}
          {!isPaidUser && (
            <div style={{ position: 'absolute', top: 14, right: 14 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 100,
                background: 'rgba(22,163,74,0.10)', color: '#16a34a',
                border: '1px solid rgba(22,163,74,0.25)',
                fontSize: 11, fontWeight: 700,
              }}>
                <Icon name="CheckCircle2" size={12} color="#16a34a" />
                Current
              </span>
            </div>
          )}

          <h3 className="text-lg font-semibold text-foreground mb-2">Free Plan</h3>
          <div className="flex items-baseline mb-4">
            <span className="text-3xl font-bold text-foreground">$0</span>
          </div>

          <ul className="space-y-2 mb-6">
            {freeFeatures.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PREMIUM CARD with dynamic pricing - showing both currencies */}
        <div style={{
          position: 'relative', borderRadius: 14, padding: 24,
          border: isPaidUser ? '2px solid transparent' : '1.5px solid rgb(0,130,251)',
          background: isPaidUser
            ? `linear-gradient(var(--card, #fff), var(--card, #fff)) padding-box, ${GRAD} border-box`
            : 'linear-gradient(160deg, rgba(0,130,251,0.06) 0%, rgba(0,74,173,0.09) 100%)',
          transition: 'all 0.15s',
          overflow: 'visible',
        }}>

          {/* Most Popular badge */}
          <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
            <span style={{
              padding: '4px 14px', borderRadius: 100,
              background: GRAD, color: '#fff',
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              boxShadow: GRAD_SHADOW, whiteSpace: 'nowrap',
            }}>
              Most Popular
            </span>
          </div>

          {/* Current badge */}
          {isPaidUser && (
            <div style={{ position: 'absolute', top: 14, right: 14 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 100,
                background: 'rgba(22,163,74,0.10)', color: '#16a34a',
                border: '1px solid rgba(22,163,74,0.25)',
                fontSize: 11, fontWeight: 700,
              }}>
                <Icon name="CheckCircle2" size={12} color="#16a34a" />
                Current
              </span>
            </div>
          )}

          {/* Label */}
          <p style={{
            margin: '0 0 10px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.09em', color: 'rgb(0,130,251)',
            textTransform: 'uppercase',
          }}>
            Premium Plan
          </p>

          {/* Strikethrough row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#94a3b8', textDecoration: 'line-through' }}>
              ${finalUsdOriginal.toLocaleString('en-US')}
            </span>
            <span style={{
              background: '#fef3c7', border: '1px solid #fde68a',
              color: '#92400e', fontSize: 10, fontWeight: 700,
              padding: '2px 7px', borderRadius: 6,
            }}>
              {priceData.discountPercent}% OFF
            </span>
          </div>

          {/* Offer price - showing both USD and INR */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 36, fontWeight: 700, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.02em' }}>
              ${finalUsdOffer.toLocaleString('en-US')}
            </span>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>
              or ₹{finalInrOffer.toLocaleString('en-IN')}
              {priceData.gstPercentage && priceData.gstPercentage > 0 && (
                <span style={{ fontSize: 10, marginLeft: 4, color: '#10b981' }}>
                  (incl. {priceData.gstPercentage}% GST)
                </span>
              )}
            </span>
          </div>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>Per audit · One-time</p>

          {/* Savings callout - showing both currencies */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'linear-gradient(135deg, rgba(9,10,11,0.04), rgba(0,130,251,0.08))',
            border: '1px solid rgba(0,130,251,0.25)', borderRadius: 8,
            padding: '4px 10px', marginBottom: 14,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <path d="M12 2H7a2 2 0 0 0-2 2v5l9 9 7-7-9-9z" stroke="#004AAD" strokeWidth="2" strokeLinejoin="round"/>
              <circle cx="8.5" cy="8.5" r="1" fill="#004AAD"/>
            </svg>
            <span style={{
              fontSize: 11, fontWeight: 700,
              background: 'linear-gradient(135deg, #004AAD, #0082FB)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              You save ${usdSavings.toLocaleString('en-US')} · ₹{inrSavings.toLocaleString('en-IN')} on this order
            </span>
          </div>

          {/* AI + Human badge */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 100, padding: '4px 12px',
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="#f59e0b"/>
              </svg>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>AI + Human Powered</span>
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-2 mb-6">
            {priceData.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{f}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          {isPaidUser ? (
            <button
              onClick={() => navigate('/initial-form-upload-screen')}
              style={{
                width: '100%', padding: '11px 0', borderRadius: 100,
                border: '1.5px solid rgb(0,130,251)',
                background: 'transparent', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                color: 'rgb(0,130,251)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,130,251,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon name="Upload" size={15} />
              Submit Another Audit
            </button>
          ) : (
            <button
              onClick={() => navigate('/initial-form-upload-screen')}
              style={{
                width: '100%', padding: '12px 0', borderRadius: 100,
                border: 'none', cursor: 'pointer',
                background: GRAD, color: '#fff',
                fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                boxShadow: GRAD_SHADOW,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'filter 0.15s, transform 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
            >
              Upgrade to Premium
              <Icon name="ArrowRight" size={15} color="#fff" />
            </button>
          )}
        </div>
      </div>

      {/* ── Billing Info ── */}
      <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Pay-Per-Audit Model</p>
            <p>Premium audits are charged per submission. No recurring subscriptions or hidden fees. You only pay when you need expert analysis.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPanel;